import { Timestamps, Versioned } from './shared.types';

/**
 * Documents, photos and signatures of the Orders & Transfers module
 * (docs/plans/orders-and-transfers.md §4.8), kept in a private bucket.
 */
export enum AttachmentKind {
  SIGNATURE = 'SIGNATURE',
  DOCUMENT = 'DOCUMENT',
  PROOF = 'PROOF',
  INVOICE = 'INVOICE',
  QUOTATION = 'QUOTATION',
  /** A voice note (a manual task's recording). */
  AUDIO = 'AUDIO',
}

/** INTENT until the uploaded file is checked, then VERIFIED or REJECTED. */
export enum AttachmentStatus {
  INTENT = 'INTENT',
  VERIFIED = 'VERIFIED',
  REJECTED = 'REJECTED',
}

/** What an attachment belongs to. The flow using it checks the match. */
export enum AttachmentEntityType {
  STOCK_ORDER = 'STOCK_ORDER',
  TRANSFER = 'TRANSFER',
  PURCHASE_ORDER = 'PURCHASE_ORDER',
  GOODS_RECEIPT = 'GOODS_RECEIPT',
  SUPPLIER_PAYMENT = 'SUPPLIER_PAYMENT',
  /** A manual task (§4.13); `entity_id` is the ULID the client made for it. */
  TASK = 'TASK',
}

/** The file types accepted, recognised by their first bytes on confirm. */
export enum AttachmentContentType {
  PDF = 'application/pdf',
  JPEG = 'image/jpeg',
  PNG = 'image/png',
  WEBP = 'image/webp',
  HEIC = 'image/heic',
  /** Recorded in the browser (Chrome, Edge, Firefox, Safari). */
  WEBM_AUDIO = 'audio/webm',
  OGG_AUDIO = 'audio/ogg',
  MP4_AUDIO = 'audio/mp4',
}

export interface Attachment extends Timestamps, Versioned {
  id: string;
  merchant_id: string;
  store_id: string;
  entity_type: AttachmentEntityType;
  entity_id: string;
  kind: AttachmentKind;
  /** The name shown when the file is opened. */
  file_name: string;
  /** Declared at intent; the upload link only accepts this type and size. */
  expected_content_type: AttachmentContentType;
  expected_size: number;
  status: AttachmentStatus;
  /** Why the file was refused on confirm (REJECTED only). */
  rejected_reason?: string;
  verified_at?: string;
  created_by_id: string;
  created_by_name: string;
}

/**
 * `GET /inventory-transfers/{merchant_id}/attachments/{attachment_id}`:
 * service-only (IAM), for a service that references a file (a manual task)
 * to check its status, owner entity and store.
 */
export interface GetAttachment {
  merchant_id: string;
  attachment_id: string;
}

/** `POST /inventory-transfers/{merchant_id}/attachments` */
export interface CreateAttachmentIntent {
  store_id: string;
  entity_type: AttachmentEntityType;
  entity_id: string;
  kind: AttachmentKind;
  file_name: string;
  content_type: AttachmentContentType;
  /** Bytes, at most 10 MB. */
  size: number;
}

export interface AttachmentIntentResult {
  attachment: Attachment;
  /** PUT the file here within 10 minutes, with exactly `upload_headers`. */
  upload_url: string;
  upload_headers: Record<string, string>;
  expires_at: string;
}

/** `GET /inventory-transfers/{merchant_id}/attachments/{attachment_id}/url` */
export interface AttachmentViewUrl {
  attachment: Attachment;
  /** Opens the file inline for 15 minutes. */
  url: string;
  expires_at: string;
}

/**
 * A signature bound to what was signed: `document_hash` is the SHA-256 of
 * the canonical JSON of the signed content (`documentHash`). Changing any
 * signed value, or the document's version, voids it (`isSignatureCurrent`).
 */
export interface SignatureRecord {
  /** Who signs in the flow, e.g. `SENDER`, `DRIVER`, `RECEIVER`. */
  role: string;
  /** A VERIFIED SIGNATURE attachment holding the drawn signature. */
  attachment_id: string;
  signer_id: string;
  signer_name: string;
  signed_at: string;
  document_hash: string;
  document_version: number;
  method: 'CANVAS';
}

export interface AttachmentDBItem extends Attachment {
  PK: string;
  SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
  /** The object's key in the private bucket. */
  key: string;
  /** Epoch seconds: an INTENT never confirmed is purged after 24 h. */
  ttl?: number;
}
